import type { Insurance, PaxType, PriceGroup, SearchResult, SortKey } from '@optionway/models';
import type { TagPlanEcommerce, TagPlanItem, TagPlanSearchContext, FunnelPage } from '#imports';
import { DateTime } from 'luxon';

const ITEM_LIST_ID = 'search_results';
const IMPRESSION_LIMIT = 10;

interface TagPlanListContext {
  search_results_count: number;
  active_filters_count: number;
  sort_by: SortKey;
}

// The domain in GA4's vocabulary: the committed search as event parameters, the offers and the cart as
// items[]. Getters only — useGtmStore is the one that pushes. Payload contract: docs/tag-plan.md.
export const useTagPlanStore = defineStore('tag-plan', {
  getters: {
    // Committed criteria, never the live form: the snapshot taken when Search was clicked.
    searchContext(): TagPlanSearchContext {
      const criteria = useSearchCriteriaStore();
      const key = criteria.committedCriteriaKey;
      const origin = criteria.committedOriginCode;
      const destination = criteria.committedDestinationCode;
      const departureDate = criteria.committedDepartureDate;

      return {
        trip_type: key.tripType,
        cabin_class: key.cabinClass,
        origin,
        destination,
        route: tagPlanRoute(origin, destination, !criteria.committedIsOneWay),
        departure_date: departureDate,
        return_date: criteria.committedReturnDate,
        booking_window: bookingWindow(DateTime.now().toISODate(), departureDate),
        pax_adults: key.adults,
        pax_children: key.children,
        pax_infants: key.infants,
        pax_total: criteria.committedPaxCount,
        legs_count: key.legs.length,
      };
    },

    offerItem(): (result: SearchResult, index?: number) => TagPlanItem {
      const context = this.searchContext;

      return (result, index) => flightTagPlanItem(result, context, index);
    },

    listContext(): TagPlanListContext {
      return {
        search_results_count: useSearchResultIndexStore().availableSearchResults.length,
        active_filters_count: useSearchResultFiltersStore().activeFiltersCount,
        sort_by: useSortOptionsStore().sortBy,
      };
    },

    resultsImpression(): TagPlanEcommerce {
      const results = useSearchResultIndexStore().availableSearchResults;

      return {
        item_list_id: ITEM_LIST_ID,
        items: results.slice(0, IMPRESSION_LIMIT).map((result, position) => this.offerItem(result, position)),
      };
    },

    // The rank is resolved from the rendered list here, so the offer card stays unaware of its position.
    offerSelection(): (offerUuid: string) => TagPlanEcommerce | undefined {
      const results = useSearchResultIndexStore().availableSearchResults;

      return (offerUuid) => {
        const index = results.findIndex((result) => result.offerUuid === offerUuid);
        const result = results[index];
        if (!result) {
          return;
        }

        return { item_list_id: ITEM_LIST_ID, items: [this.offerItem(result, index)] };
      };
    },

    // The priced fare, which may differ from the listed one.
    pricedOffer(): TagPlanEcommerce | undefined {
      const priced = useSelectedOfferStore().offer;
      if (!priced?.flight) {
        return;
      }

      return {
        currency: TAG_PLAN_CURRENCY,
        value: priced.flight.price?.total,
        items: [this.offerItem(priced.flight)],
      };
    },

    // One GA4 item per bag type the traveller holds, built from the cart's own facts.
    bagTagPlanItems(): TagPlanItem[] {
      const luggages = useLuggagesStore();
      return luggages.selectedBagTypes.map((type) =>
        bagTagPlanItem(type, luggages.selectedCount(type), luggages.selectedBagsPriceByType(type)),
      );
    },

    // items[] of add_payment_info and purchase.
    cartItems(): TagPlanItem[] {
      const flight = this.pricedOffer?.items ?? [];
      const insurance = useSelectedInsuranceStore().insurance;
      const ancillaries = ancillaryTagPlanItems(
        this.bagTagPlanItems,
        isCartInsurance(insurance) ? insurance : undefined,
      );

      const contribution = useSelectedContributionStore().contribution;
      if (contribution) {
        ancillaries.push(contributionTagPlanItem(contribution));
      }

      return [...flight, ...ancillaries];
    },

    // What the baggage step holds once it is completed, so the attach rate and its value read from
    // one event instead of summing add_to_cart and remove_from_cart deltas.
    baggageEcommerce(): TagPlanEcommerce | undefined {
      const items = this.bagTagPlanItems;
      if (items.length === 0) {
        return;
      }

      return {
        currency: TAG_PLAN_CURRENCY,
        // The cart's own total, not a sum of unit prices: no rounding drift reaches the revenue.
        value: roundToCents(useLuggagesStore().selectedBagsTotalPrice),
        items,
      };
    },

    // Each named step completion carries what that step decided; a step with nothing to describe carries nothing.
    stepEcommerce(): (page: FunnelPage) => TagPlanEcommerce | undefined {
      const selections: Partial<Record<FunnelPage, TagPlanEcommerce | undefined>> = {
        luggages: this.baggageEcommerce,
        insurances: insuranceEcommerce(useSelectedInsuranceStore().insurance),
      };

      return (page) => selections[page];
    },

    // Reused verbatim as the purchase payload through the checkout handover, which is why it carries tax.
    checkoutEcommerce(): TagPlanEcommerce {
      const cart = useShoppingCartStore();

      return {
        currency: TAG_PLAN_CURRENCY,
        value: cart.amountToPay,
        tax: totalTax(cart.passengerPriceItems),
        items: this.cartItems,
      };
    },
  },
});

const FLIGHT = 'flight';

// Zero-padded: GA4 sorts dimension values as strings. Band rationale: docs/tag-plan.md § Booking window.
const BOOKING_WINDOW_BOUNDS = [
  { upTo: 3, label: '000-003' },
  { upTo: 7, label: '004-007' },
  { upTo: 14, label: '008-014' },
  { upTo: 30, label: '015-030' },
  { upTo: 60, label: '031-060' },
  { upTo: 90, label: '061-090' },
  { upTo: 180, label: '091-180' },
];

function tagPlanRoute(origin?: string, destination?: string, isRoundTrip = false): string | undefined {
  if (!origin || !destination) {
    return;
  }
  return isRoundTrip ? `${origin}-${destination}-${origin}` : `${origin}-${destination}`;
}

// `now` is injected rather than read here so the function stays pure — same convention as birthdate-validation.
function bookingWindow(now: string, departureDate?: string): string | undefined {
  if (!departureDate) {
    return;
  }
  const departure = DateTime.fromISO(departureDate);
  const reference = DateTime.fromISO(now);
  if (!departure.isValid || !reference.isValid) {
    return;
  }

  // diff(…, 'days') is calendar-aware; .as('days') loses a day across the spring DST change.
  const days = Math.floor(departure.startOf('day').diff(reference.startOf('day'), 'days').days);
  if (days < 0) {
    return;
  }

  return BOOKING_WINDOW_BOUNDS.find((bound) => days <= bound.upTo)?.label ?? '181_PLUS';
}

function stopsBucket(stops?: number): string {
  if (stops === undefined || stops <= 0) {
    return 'DIRECT';
  }
  return stops === 1 ? '1_STOP' : '2_PLUS_STOPS';
}

function flightTagPlanItem(result: SearchResult, context: TagPlanSearchContext, index?: number): TagPlanItem {
  return {
    item_id: result.offerUuid,
    item_name: context.route,
    item_brand: result.legs[0]?.carrier?.iataCode,
    item_variant: context.cabin_class,
    item_category: FLIGHT,
    trip_type: context.trip_type,
    stops_bucket: stopsBucket(Math.max(0, ...result.legs.map((leg) => leg.stopover?.count ?? 0))),
    booking_window: context.booking_window,
    price: result.price?.averagePerPax,
    quantity: result.price?.paxCount,
    // A rank only means something in a list: the conversion events carry none.
    index: index === undefined ? undefined : index + 1,
  };
}

function ancillaryTagPlanItems(bagItems: TagPlanItem[], insurance?: Insurance): TagPlanItem[] {
  const items = [...bagItems];
  if (insurance) {
    items.push(insuranceTagPlanItem(insurance));
  }
  return items;
}

// Approximate: the model exposes taxes for the first passenger of each group only.
function totalTax(groups: Partial<Record<PaxType, PriceGroup>>): number {
  let tax = 0;
  for (const group of Object.values(groups)) {
    tax += (group.paxDetail.taxes ?? 0) * (group.count ?? 0);
  }
  return roundToCents(tax);
}
